/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { FieldWrapper } from "../FieldWrapper";
import { CodeInput, ICodeEditorProps, Icon, Input, InputAdornment, InputButton } from "../../core";
import { IDocumentError } from "@adapptio/lib-blueprint";
import { IFieldValidationError } from "../FieldTypes";
import { isFunction } from "@adapptio/lib-shared";

import "./FieldCode.scss";
import { FieldStatus } from "../FieldStatus";

export interface IFieldCodeProps {
	id?: string;
	label?: string;
	value: string;
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	parseErrors?: IDocumentError[];
	resultData?: unknown;
	className?: string;
	onChange?: (value: string) => void;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	grow?: boolean;
	inputRef?: React.MutableRefObject<HTMLInputElement>;
	language?: ICodeEditorProps["language"];
	multiLine?: boolean;
	height?: number;
	debounceTimeout?: number;
	canExpand?: boolean;
	isExpanded?: boolean;
	onExpand?: () => void;
	clearable?: boolean;
	onEditorMount?: ICodeEditorProps["onMount"];
	onOuterReferenceClick?: ICodeEditorProps["onOuterReferenceClick"];
}

export const FieldCode: React.FC<IFieldCodeProps> = ({
	className,
	onExpand,
	isExpanded,
	canExpand,
	onChange,
	clearable,
	validationErrors,
	onEditorMount,
	onOuterReferenceClick,
	...props
}) => {
	const baseClass = BEM("edt-field-code");

	const adornment = canExpand || clearable || validationErrors?.length > 0;

	if (className) {
		baseClass.add(className);
	}

	const handleEditorExpandClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
		if (canExpand && isFunction(onExpand)) {
			onExpand();
		}
	}, [ canExpand, onExpand ]);

	const handleClearClick = useCallback(() => {
		if (isFunction(onChange)) {
			onChange(null);
		}
	}, [ onChange ]);

	return (
		<FieldWrapper type="code">
			<Input
				{...props}
				onChange={onChange}
				inputComponent={CodeInput}
				onMount={onEditorMount}
				onOuterReferenceClick={onOuterReferenceClick}
				endAdornment={
					adornment && (
						<InputAdornment>
							{validationErrors?.length > 0 && <FieldStatus errors={validationErrors} />}
							{clearable && (
								<InputButton
									className={baseClass.element("editor-clear").toClassName()}
									onClick={handleClearClick}
									tabIndex={-1}
									disabled={!props?.value}
								>
									<Icon
										className={baseClass.element("editor-clear-icon").toClassName()}
										name="mdi/close"
									/>
								</InputButton>
							)}
							{canExpand && (
								<InputButton
									className={baseClass.element("editor-popup-trigger").toClassName()}
									onClick={handleEditorExpandClick}
									tabIndex={-1}
									selected={isExpanded}
								>
									<Icon
										className={baseClass
											.element("editor-popup-trigger-icon")
											.toClassName()}
										name="mdi/open-in-new"
									/>
								</InputButton>
							)}
						</InputAdornment>
					)
				}
			/>
		</FieldWrapper>
	);
};
